// Projects portfolio (RF-UI-07/08, AC-69): search, filters with counts, grid/list toggle,
// cards with honest preview placeholder, aggregate state, scoped progress and the main
// action (Follow / Resolve access / Open). Loading, empty, error and pagination states.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { ProjectSummary } from "@maestros/contracts";
import { useI18n } from "../i18n/index.tsx";
import { scopeLabel } from "../lib/format.ts";
import { listProjects, type ProjectListQuery } from "../api/endpoints.ts";
import type { ProjectCounts } from "../api/types.ts";
import { AGGREGATE_VIEW } from "../lib/status.ts";
import { readLocal, writeLocal } from "../lib/storage.ts";
import { useDebouncedCallback } from "../lib/useAsync.ts";
import { useRealtimeEvents } from "../realtime/RealtimeProvider.tsx";
import { Button, IconButton } from "../components/Button.tsx";
import { EmptyState, ErrorBanner, Skeleton } from "../components/Feedback.tsx";
import { ProgressBar, StatusPill } from "../components/Status.tsx";
import { CreateProjectDialog } from "../features/CreateProjectDialog.tsx";
import { useOrgRole } from "../features/config/shared.tsx";
import { StaleDataNotice } from "../shell/ConnectionStatus.tsx";
import { AlertTriangleIcon, ArrowRightIcon, FolderIcon, GitBranchIcon, GridIcon, ListIcon, MonitorIcon, PlusIcon, SearchIcon } from "../components/icons.tsx";

type Filter = NonNullable<ProjectListQuery["filter"]>;
const FILTERS: Filter[] = ["all", "running", "needs_attention", "completed"];

function primaryAction(p: ProjectSummary): { kind: "resolve" | "follow" | "open"; to: string } {
  if (p.aggregate_state === "needs_attention" || p.aggregate_state === "blocked") return { kind: "resolve", to: `/projects/${p.id}/settings` };
  if (p.aggregate_state === "running") return { kind: "follow", to: `/projects/${p.id}` };
  return { kind: "open", to: `/projects/${p.id}` };
}

function matchesFilter(p: ProjectSummary, f: Filter): boolean {
  if (f === "running") return p.aggregate_state === "running";
  if (f === "needs_attention") return p.aggregate_state === "needs_attention" || p.aggregate_state === "blocked";
  if (f === "completed") return p.aggregate_state === "completed";
  return true;
}

function countsFrom(items: ProjectSummary[]): ProjectCounts {
  return {
    all: items.length,
    running: items.filter((p) => p.aggregate_state === "running").length,
    needs_attention: items.filter((p) => p.aggregate_state === "needs_attention" || p.aggregate_state === "blocked").length,
    completed: items.filter((p) => p.aggregate_state === "completed").length,
  };
}

function ProjectCard({ project, view }: { project: ProjectSummary; view: "grid" | "list" }) {
  const { t, formatRelative } = useI18n();
  const state = AGGREGATE_VIEW[project.aggregate_state];
  const action = primaryAction(project);
  const { accepted_required: done, total_required: total, scope_label: scope } = project.progress;
  const headingId = `project-${project.id}-name`;
  return (
    <li className={`project-card card project-card--${view}`} aria-labelledby={headingId} data-testid="project-card">
      <div className="project-preview" role="img" aria-label={t("projects.noPreview")}>
        <MonitorIcon size={28} />
        <span>{t("projects.noPreview")}</span>
      </div>
      <div className="project-card-body">
        <h2 id={headingId} className="project-card-title">
          <Link to={`/projects/${project.id}`} className="project-card-link">
            {project.name}
          </Link>
        </h2>
        <p className="project-card-desc">{project.description || t("projects.noDescription")}</p>
        <div className="row row--wrap row--between">
          <StatusPill view={state} />
          <span className="subtle project-card-activity">
            {project.last_activity_at ? t("projects.lastActivity", { when: formatRelative(project.last_activity_at) }) : t("projects.noActivity")}
          </span>
        </div>
        {project.attention_reason ? (
          <p className="project-card-attention">
            <AlertTriangleIcon size={14} />
            <span>{project.attention_reason}</span>
          </p>
        ) : null}
        <ProgressBar
          value={done}
          max={Math.max(total, 1)}
          label={total > 0 ? t("projects.progress", { done, total }) : t("projects.noRequiredTasks")}
          detail={total > 0 ? t("projects.scope", { scope: scopeLabel(t, scope) }) : undefined}
        />
        <div className="row row--between project-card-footer">
          <span className="muted" style={{ fontSize: "var(--text-sm)" }}>
            {t("projects.activeAgents", { n: project.active_agents })}
          </span>
          <Link
            to={action.to}
            className={`btn btn--sm ${action.kind === "resolve" ? "btn--outline-accent" : "btn--outline-accent"}`}
            aria-label={`${t(`projects.action.${action.kind}`)} — ${project.name}`}
          >
            {t(`projects.action.${action.kind}`)}
            <ArrowRightIcon size={14} />
          </Link>
        </div>
      </div>
    </li>
  );
}

export function ProjectsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = (FILTERS.includes(params.get("filter") as Filter) ? params.get("filter") : "all") as Filter;
  const search = params.get("q") ?? "";
  const [searchInput, setSearchInput] = useState(search);
  const [view, setView] = useState<"grid" | "list">(() => (readLocal<string>("projectsView", "grid") === "list" ? "list" : "grid"));
  const [items, setItems] = useState<ProjectSummary[]>([]);
  const [counts, setCounts] = useState<ProjectCounts | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);
  const [creating, setCreating] = useState(false);
  const [fromGithub, setFromGithub] = useState(false);
  // POST /v1/projects/from-github requires an organization admin: the button is not offered to anyone else.
  const isOrgAdmin = useOrgRole().atLeast("admin");

  useEffect(() => {
    const controller = new AbortController();
    if (items.length === 0) setLoading(true);
    setError(null);
    listProjects({ search, filter, limit: 100 }, controller.signal)
      .then((page) => {
        setItems(page.items);
        setNextCursor(page.next_cursor);
        setCounts(page.counts ?? null);
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted) setError(e);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filter, tick]);

  const applySearch = useDebouncedCallback(() => {
    const next = new URLSearchParams(params);
    if (searchInput.trim()) next.set("q", searchInput.trim());
    else next.delete("q");
    setParams(next, { replace: true });
  }, 300);

  useEffect(() => {
    if (searchInput !== search) applySearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const refresh = useDebouncedCallback(() => setTick((n) => n + 1), 1500);
  useRealtimeEvents((e) => {
    if (e.type.startsWith("project.") || e.type.startsWith("task.")) refresh();
  });

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const page = await listProjects({ search, filter, cursor: nextCursor, limit: 100 });
      setItems((prev) => [...prev, ...page.items.filter((p) => !prev.some((q) => q.id === p.id))]);
      setNextCursor(page.next_cursor);
    } catch (e) {
      setError(e);
    } finally {
      setLoadingMore(false);
    }
  };

  // Server counts when provided; fallback (older API): counts over the loaded list, shown
  // only for the unfiltered, fully loaded list (never an invented total).
  const shownCounts = useMemo(() => counts ?? (!nextCursor && filter === "all" ? countsFrom(items) : null), [counts, nextCursor, filter, items]);
  const visible = useMemo(() => items.filter((p) => matchesFilter(p, filter)), [items, filter]);

  const setFilter = (f: Filter) => {
    const next = new URLSearchParams(params);
    if (f === "all") next.delete("filter");
    else next.set("filter", f);
    setParams(next, { replace: true });
  };

  const changeView = (v: "grid" | "list") => {
    setView(v);
    writeLocal("projectsView", v);
  };

  return (
    <div className="page projects">
      <header className="page-header">
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="page-title">{t("projects.title")}</h1>
          <p className="page-subtitle">{t("projects.subtitle")}</p>
        </div>
        <div className="row row--wrap">
          {isOrgAdmin ? (
            <Button size="lg" icon={<GitBranchIcon size={18} />} onClick={() => setFromGithub(true)}>
              {t("githubOnboarding.newFromGithub")}
            </Button>
          ) : null}
          <Button variant="primary" size="lg" icon={<PlusIcon size={18} />} onClick={() => setCreating(true)}>
            {t("projects.new")}
          </Button>
        </div>
      </header>

      <div className="projects-toolbar">
        <label className="search">
          <SearchIcon size={18} />
          <span className="sr-only">{t("projects.search")}</span>
          <input type="search" className="input" placeholder={t("projects.searchPlaceholder")} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </label>
        <div className="filter-group" role="group" aria-label={t("projects.filters")}>
          {FILTERS.map((f) => (
            <button key={f} type="button" className="filter-btn" aria-pressed={filter === f} onClick={() => setFilter(f)} data-filter={f}>
              {t(`projects.filter.${f}`)}
              {shownCounts ? (
                <span className="count" aria-label={t("projects.countLabel", { n: shownCounts[f] })}>
                  {shownCounts[f]}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        <div className="view-toggle" role="group" aria-label={t("projects.viewMode")}>
          <IconButton label={t("projects.viewGrid")} aria-pressed={view === "grid"} onClick={() => changeView("grid")}>
            <GridIcon />
          </IconButton>
          <IconButton label={t("projects.viewList")} aria-pressed={view === "list"} onClick={() => changeView("list")}>
            <ListIcon />
          </IconButton>
        </div>
      </div>

      <StaleDataNotice />

      {loading ? (
        <ul className={`project-${view}`} aria-busy="true" aria-label={t("common.loading")}>
          {Array.from({ length: 6 }, (_, i) => (
            <li key={i} className="card project-card">
              <Skeleton height={140} style={{ borderRadius: "var(--radius) var(--radius) 0 0" }} />
              <div className="stack" style={{ padding: 16 }}>
                <Skeleton height={20} width="50%" />
                <Skeleton height={14} width="80%" />
                <Skeleton height={8} />
              </div>
            </li>
          ))}
        </ul>
      ) : error && items.length === 0 ? (
        <ErrorBanner error={error} onRetry={() => setTick((n) => n + 1)} title={t("projects.loadFailed")} />
      ) : visible.length === 0 && !nextCursor ? (
        <div className="card">
          {search || filter !== "all" ? (
            <EmptyState icon={<SearchIcon />} title={t("projects.noMatchTitle")}>
              <p>{t("projects.noMatchBody")}</p>
              <div className="empty-state-actions">
                <Button
                  onClick={() => {
                    setSearchInput("");
                    setParams(new URLSearchParams(), { replace: true });
                  }}
                >
                  {t("projects.clearFilters")}
                </Button>
              </div>
            </EmptyState>
          ) : (
            <EmptyState
              icon={<FolderIcon />}
              title={t("projects.emptyTitle")}
              actions={
                <>
                  <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => setCreating(true)}>
                    {t("projects.new")}
                  </Button>
                  <Button onClick={() => navigate("/")}>{t("projects.startFromHome")}</Button>
                  <Button variant="ghost" onClick={() => navigate("/settings")}>
                    {t("home.configureInfra")}
                  </Button>
                </>
              }
            >
              <p>{t("projects.emptyBody")}</p>
            </EmptyState>
          )}
        </div>
      ) : (
        <>
          {error ? <ErrorBanner error={error} onRetry={() => setTick((n) => n + 1)} /> : null}
          <ul className={`project-${view}`} aria-label={t("projects.listLabel")}>
            {visible.map((p) => (
              <ProjectCard key={p.id} project={p} view={view} />
            ))}
          </ul>
          {nextCursor ? (
            <div className="load-more stack" style={{ alignItems: "center" }}>
              {filter !== "all" && !counts ? <p className="field-hint">{t("projects.filterLoadedOnly")}</p> : null}
              <Button onClick={() => void loadMore()} loading={loadingMore}>
                {t("projects.loadMore")}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <CreateProjectDialog open={creating} onClose={() => setCreating(false)} />
      <CreateProjectDialog open={fromGithub} onClose={() => setFromGithub(false)} mode="github" />
    </div>
  );
}
